'use client';

import { useEffect, useRef } from 'react';
import { useSSE } from '../SseProvider';
import { useTerminalExecution } from '../../../hooks/useTerminalExecution';
import {
  EXTERNAL_TOOL_BATCH_SSE_EVENT,
  ExternalToolBatchPayloadSchema,
  PORTAL_TOOL_EXECUTE_SSE_EVENT,
  PORTAL_TOOL_NAVIGATE_EVENT,
  TERMINAL_EXECUTE_SSE_EVENT,
  PortalToolNavigationSchema,
} from '@terra/shared-dto-chat-types';
import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  bindForegroundSessionQueues,
  createSessionTaskQueue,
  createTerminalExecutionQueue,
  createTerminalResumeQueue,
  scheduleTerminalResume,
} from '../terminal-execution-queue';
import {
  bindExternalToolResultCoordinatorReset,
  createExternalToolResultCoordinator,
} from '../external-tool-result-coordinator';
import { useExternalToolActivityStore } from '../../../store/externalToolActivityStore';
import { registerPortalToolHandlers } from './register-portal-tool-handlers';
import { hasActiveChat } from '../../../store/chat-instance-presence';
import {
  isPortalToolSessionSuppressed,
  sendMessageWithRetry,
} from './portal-tool-send-message';
import { createTerminalExecutionHandler } from './handlers/terminal-execution.handler';
import { withTargetTunnelGuard } from './handlers/target-tunnel-guard';
import { isForegroundChatWorkSuppressed } from '../terminal-resume-suppress';
import { openBatchParamsFromSsePayload } from './portal-external-tool-batch';
import { handlePortalToolExecute } from './portal-tool-execute';
import { handlePortalTerminalExecute } from './portal-terminal-execute';
import { rebindInFlightTerminalExecutes } from './portal-terminal-foreground-run';
import { runPortalTerminalCommand } from './portal-terminal-run-command';
import { forgetInFlightTerminalExecutesForChat } from './terminal-inflight-executes';
import { navigateToPortalToolTarget } from '../../../utils/portalToolNavigation';
import { createPortalToolExecutionTracker } from './portal-tool-handler.utils';

export function PortalToolProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const tCommon = useTranslations('common');
  const tCopilot = useTranslations('chat.copilot');
  const { subscribe } = useSSE();
  const { ensureTerminalSession, isElectronAvailable } = useTerminalExecution();
  const processedToolCallsRef = useRef(createPortalToolExecutionTracker());
  const ensureTerminalSessionRef = useRef(ensureTerminalSession);
  const terminalExecutionQueueRef = useRef(createTerminalExecutionQueue());
  const externalToolResumeQueueRef = useRef(createTerminalResumeQueue());
  const portalExecutionQueueRef = useRef(
    createSessionTaskQueue({
      errorMessage: '[PortalTool] Queued portal execution failed for session',
    }),
  );
  const externalToolResultCoordinatorRef = useRef(
    createExternalToolResultCoordinator({
      sendMessage: async (chatId, message) => {
        await scheduleTerminalResume({
          sessionId: chatId,
          queue: externalToolResumeQueueRef.current,
          send: async () => {
            const sent = await sendMessageWithRetry(chatId, message);

            if (!sent) {
              throw new Error('Failed to send external tool results to chat');
            }
          },
        });
      },
      onChatActivityChange: ({ chatId, active }) => {
        useExternalToolActivityStore
          .getState()
          .setChatActivity({ chatId, active });
      },
    }),
  );

  useEffect(() => {
    bindExternalToolResultCoordinatorReset({
      resetChatBatches: (chatId) => {
        externalToolResultCoordinatorRef.current.resetChatBatches(chatId);
        processedToolCallsRef.current.clearChat(chatId);
        forgetInFlightTerminalExecutesForChat(chatId);
      },
    });
    bindForegroundSessionQueues({
      queues: [
        terminalExecutionQueueRef.current,
        portalExecutionQueueRef.current,
        externalToolResumeQueueRef.current,
      ],
    });
  }, []);

  useEffect(() => {
    ensureTerminalSessionRef.current = ensureTerminalSession;
  }, [ensureTerminalSession]);

  useEffect(() => {
    registerPortalToolHandlers({
      terminalHandler: withTargetTunnelGuard({
        handler: createTerminalExecutionHandler({
          ensureTerminalSession: (params) =>
            ensureTerminalSessionRef.current(params),
        }),
      }),
    });
  }, []);

  useEffect(() => {
    const listInFlight = window.electronAPI?.command?.inFlight;

    if (!isElectronAvailable || !listInFlight) {
      return;
    }

    rebindInFlightTerminalExecutes({
      coordinator: externalToolResultCoordinatorRef.current,
      queue: terminalExecutionQueueRef.current,
      runCommand: runPortalTerminalCommand,
      listInFlight: () => listInFlight(),
    })
      .then(({ rebound, dropped }) => {
        if (rebound.length > 0 || dropped.length > 0) {
          console.info('[PortalToolProvider] Re-bound in-flight commands', {
            rebound,
            dropped,
          });
        }
      })
      .catch((error: unknown) => {
        console.warn(
          '[PortalToolProvider] Failed to re-bind in-flight commands after reload',
          { error: error instanceof Error ? error : new Error(String(error)) },
        );
      });
  }, [isElectronAvailable]);

  useEffect(() => {
    const handleNavigate = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }

      const parsed = PortalToolNavigationSchema.safeParse(event.detail);

      if (!parsed.success) {
        return;
      }

      navigateToPortalToolTarget(parsed.data);
    };

    window.addEventListener(PORTAL_TOOL_NAVIGATE_EVENT, handleNavigate);

    return () => {
      window.removeEventListener(PORTAL_TOOL_NAVIGATE_EVENT, handleNavigate);
    };
  }, []);

  useEffect(() => {
    if (!isElectronAvailable) {
      return;
    }

    const handleExternalToolBatch = (batchData: unknown) => {
      const parsed = ExternalToolBatchPayloadSchema.safeParse(batchData);

      if (!parsed.success) {
        console.warn(
          '[PortalToolProvider] Invalid external tool batch SSE payload',
          {
            error: parsed.error.message,
          },
        );

        return;
      }

      const { sessionId, chatId } = parsed.data;

      if (
        !hasActiveChat(chatId) ||
        isForegroundChatWorkSuppressed({
          chatId,
          sessionId: sessionId ?? chatId,
        }) ||
        isPortalToolSessionSuppressed(chatId)
      ) {
        return;
      }

      externalToolResultCoordinatorRef.current.openBatch(
        openBatchParamsFromSsePayload(parsed.data),
      );
    };

    const handlePortalExecute = async (payload: unknown) => {
      await handlePortalToolExecute({
        payload,
        coordinator: externalToolResultCoordinatorRef.current,
        tracker: processedToolCallsRef.current,
        queue: portalExecutionQueueRef.current,
        isSessionSuppressed: isPortalToolSessionSuppressed,
      });
    };

    const handleTerminalExecute = async (terminalData: unknown) => {
      await handlePortalTerminalExecute({
        terminalData,
        coordinator: externalToolResultCoordinatorRef.current,
        tracker: processedToolCallsRef.current,
        queue: terminalExecutionQueueRef.current,
        isSessionSuppressed: isPortalToolSessionSuppressed,
        runCommand: runPortalTerminalCommand,
      });
    };

    const unsubscribePortal = subscribe(
      PORTAL_TOOL_EXECUTE_SSE_EVENT,
      (payload) => {
        handlePortalExecute(payload).catch((error: unknown) => {
          console.warn('[PortalToolProvider] Portal SSE processing error:', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
          toasterApi.error({
            title: tCopilot('sendMessageErrorTitle'),
            description: tCommon('error'),
          });
        });
      },
    );

    const unsubscribeTerminal = subscribe(
      TERMINAL_EXECUTE_SSE_EVENT,
      (terminalData) => {
        handleTerminalExecute(terminalData).catch((error: unknown) => {
          console.warn('[PortalToolProvider] Terminal SSE processing error:', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
          toasterApi.error({
            title: tCopilot('sendMessageErrorTitle'),
            description: tCommon('error'),
          });
        });
      },
    );

    const unsubscribeBatch = subscribe(
      EXTERNAL_TOOL_BATCH_SSE_EVENT,
      (batchData) => {
        handleExternalToolBatch(batchData);
      },
    );

    return () => {
      unsubscribePortal();
      unsubscribeTerminal();
      unsubscribeBatch();
    };
  }, [subscribe, isElectronAvailable, tCommon, tCopilot]);

  return children;
}
